import { gql } from "@apollo/client";
import { attachmentFragment } from "modules/CommonFragments";

const assessmentFragment = gql`
  fragment assessmentItem on Assessment {
    id
    templateId
    assessmentTitle: title {
      id
      value
    }
    assessmentType {
      id
      value
    }
    image {
      id
      value
    }
    academicTerm {
      id
      lockingStatus
    }
    lockingInfo {
      isLocked
    }
    learningCourse {
      id
    }
    assignments {
      edges {
        id
        course {
          id
        }
      }
    }
  }
`;

export const staffNameFragment = {
  staffNameFields: gql`
    fragment staffNameFields on Staff {
      firstName
      middleName
      lastName
      preferredName
      prefix
      suffix
      pronouns
      type
    }
  `,
};

export const studentNameFragment = {
  studentNameFields: gql`
    fragment studentNameFields on Student {
      firstName
      middleName
      lastName
      preferredName
      prefix
      suffix
      pronouns
      type
    }
  `,
};

export const timetableSlotFragment = {
  basicCourseDetails: gql`
    fragment courseItem on Course {
      id
      title
      isArchived
      curriculumProgram {
        id
        type
        label
      }
      primaryGrade {
        id
        curriculumProgram {
          id
          type
        }
      }
      learningCourse {
        id
        title
        gradingPeriods(filters: $termFilters) {
          totalCount
          edges {
            node {
              id
              label
              lockingStatus
            }
          }
        }
        primaryGrade {
          id
        }
      }
      students {
        totalCount
      }
      grades {
        id
      }
    }
  `,
  period: gql`
    fragment periodItem on Period {
      id
      label
      type
      isDefault
      startTime
      endTime
      sequence
      isDefault
      abbreviation
    }
  `,
  basicStaffDetails: gql`
    fragment staffItem on Staff {
      id
      ...staffNameFields
      profileImage
      email
      role
      isArchived
    }
    ${staffNameFragment.staffNameFields}
  `,
};

export const slotItemFragment = gql`
  fragment slotItem on TimetableSlotItem {
    id
    itemType
    item {
      ... on LearningCourseFlow {
        id
        label
        learningCourse {
          id
        }
        itemType
        state
        sharedDetails {
          class {
            id
          }
          students {
            id
          }
        }
        lockingInfo {
          isLocked
        }
        item {
          ... on Attachment {
            ...attachmentItem
          }
          ... on Assessment {
            ...assessmentItem
          }
        }
      }
      ... on Assignment {
        id
        contentType
        content {
          ... on Assessment {
            assignments {
              edges {
                id
                course {
                  id
                }
              }
            }
            ...assessmentItem
          }
        }
      }
    }
  }
  ${assessmentFragment}
  ${attachmentFragment.attachment}
`;

export const selectedPeriodFragment = {
  selectedPeriodsV2: gql`
    fragment selectedPeriodItemV2 on TimetableSlot {
      date
      isSelected
      day
      timetableSlotId
      rotationDay {
        id
        label
      }
      location
      course {
        ...courseItem
      }
      period {
        ...periodItem
        abbreviation
      }
      teachers {
        ...staffItem
      }
      teachersV2 {
        staff {
          ...staffItem
        }
        type
      }
      items(filters: $itemFilter) {
        ...slotItem
      }
      permissions {
        canMarkAttendance(filters: $permissionFilters)
        canEditCoursePlanner(filters: $permissionFilters)
        canShareCoursePlannerWithStudents(filters: $permissionFilters)
      }
      subject
      startTime
      endTime
      groupKey
      attendance(filters: $attendanceFilters) {
        attendanceMarkedStatus
        unmarkedStudents {
          id
        }
      }
    }
    ${timetableSlotFragment.basicCourseDetails}
    ${timetableSlotFragment.period}
    ${timetableSlotFragment.basicStaffDetails}
    ${slotItemFragment}
  `,
};
export const studentFragment = {
  studentPeriodListV2: gql`
    fragment studentPeriodListItemV2 on Student {
      id
      ...studentNameFields
      enrolledPeriodsV2(filters: $periodsFilters) {
        ...selectedPeriodItemV2
      }
    }
    ${selectedPeriodFragment.selectedPeriodsV2}
    ${studentNameFragment.studentNameFields}
  `,
};
export const staffPeriodListFragment = {
  staffPeriodListV2: gql`
    fragment staffPeriodListItemV2 on Staff {
      id
      firstName
      lastName
      middleName
      enrolledPeriodsV2(filters: $periodsFilters) {
        ...selectedPeriodItemV2
      }
    }
    ${selectedPeriodFragment.selectedPeriodsV2}
  `,
};
